import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useMedia } from "@/lib/SiteContent";

/**
 * <DynamicImage mediaKey="hero_home" /> — auto-resolves to admin-uploaded image OR falls back to default.
 *
 * Self-aligning: uses object-fit: cover within a fixed aspect-ratio container so any uploaded
 * image (regardless of dimensions) crops gracefully into the design without breaking layout.
 */
const DynamicImage = ({
  mediaKey,
  fallback,
  alt = "",
  className = "",
  imgClassName = "",
  ratio = "aspect-[16/10]",
  loading = "lazy",
  fit = "cover", // "cover" | "contain"
  position = "center", // CSS object-position
}) => {
  const fromCms = useMedia(mediaKey);
  const src = fromCms || fallback || null;

  const [loaded, setLoaded] = React.useState(false);
  const [errored, setErrored] = React.useState(false);

  // Reset state when the resolved src changes (e.g. admin uploads new image)
  React.useEffect(() => {
    setLoaded(false);
    setErrored(false);
  }, [src]);

  if (!src) {
    return (
      <div className={cn("relative overflow-hidden", ratio, className)}>
        <Skeleton className="absolute inset-0 h-full w-full bg-white/5" />
      </div>
    );
  }

  return (
    <div className={cn("relative overflow-hidden", ratio, className)}>
      {!loaded && !errored && (
        <Skeleton className="absolute inset-0 h-full w-full bg-white/5" />
      )}
      {!errored && (
        <img
          src={src}
          alt={alt}
          loading={loading}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          style={{ objectPosition: position }}
          className={cn(
            "absolute inset-0 h-full w-full transition-opacity duration-700",
            fit === "contain" ? "object-contain" : "object-cover",
            loaded ? "opacity-100" : "opacity-0",
            imgClassName
          )}
        />
      )}
      {errored && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/4 text-white/40 text-xs tracking-[0.2em] uppercase">
          Image unavailable
        </div>
      )}
    </div>
  );
};

export default DynamicImage;
